import React, { useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import * as XLSX from "xlsx";
import { ModalContainer, ModalHeader, ModalContent } from "./Modal";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import * as AppActions from "../../redux/actions/app.actions";
import { Button, Colors, Dropdown } from "@sellout/ui";
import { ButtonTypes, ButtonStates } from "@sellout/ui/build/components/Button";
import { AppNotificationTypeEnum } from "../../models/interfaces/IAppNotification";
import { media } from "@sellout/ui/build/utils/MediaQuery";
import CREATE_CONTACT from "@sellout/models/.dist/graphql/mutations/createContacts.mutation";
import { BackstageState } from "../../redux/store";

const Container = styled.div`
  position: relative;
  width: 700px;
  ${media.tablet`
    width: 100% !important;
  `};
  ${media.mobile`
    width: 100% !important;
  `};
`;

export const FileUploadContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const FileLabel = styled.label`
  background-color: #f5f5f5;
  padding: 5px 10px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s;

  &:hover {
    background-color: #eaeaea;
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const FileName = styled.span`
  font-size: 14px;
  color: #555;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 6px;
`;
export const DropZone = styled.div<{ isDragging: boolean }>`
  border: 2px dashed ${({ isDragging }) => (isDragging ? "#1e90ff" : "#ccc")};
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  background-color: ${({ isDragging }) => (isDragging ? "#f0faff" : "#f5f5f5")};
  cursor: pointer;
  transition: all 0.3s ease;
`;

const ModalFooter = styled.div<ModalContentHeight>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => (props.padding ? props.padding : "9px 20px")};
  background-color: ${Colors.White};
  border-top: 1px solid ${Colors.Grey6};
  border-radius: 0px 0px 10px 10px;
  justify-content: flex-end;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 30px;
`;
const ConfirmText = styled.div`
  font-weight: 450;
  font-size: 1.4rem;
  color: ${Colors.Grey1};
  line-height: 23px;
  padding-bottom: 20px;
`;
type ModalContentHeight = {
  height?: string;
  padding?: string;
  backgroundColor?: string;
};

interface MappedFields {
  firstName: string;
  lastName: string;
  email: string;
}
enum FieldNames {
  FirstName = "firstName",
  LastName = "lastName",
  Email = "email",
}
enum ButtonLebels {
  Cancel = "CANCEL",
  Import = "IMPORT",
}
enum ModalHeaderProp {
  ModalHeaderTitle = "Import Contacts",
  ModalContainerWidth = "560px",
}
enum DropdownFieldsErrors {
  DuplicateFields = "This header is already selected for another field.",
}
enum DropdownLabels {
  FirstName = "First Name",
  LastName = "Last Name",
  Email = "Email",
}
enum DropdownProps {
  WhiteSpace = "break-spaces",
}

const ImportTicketModal: React.FC = () => {
  /* State */
  const appState = useSelector((state: BackstageState) => state.app);
  const { modal } = appState;
  const refetchData = modal?.modalProps?.ImportContactModal[0]?.fetch;
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [mappedFields, setMappedFields] = useState<MappedFields>({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [parsedData, setParsedData] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string>("");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  /* Actions */
  const dispatch = useDispatch();
  const popModal = () => dispatch(AppActions.popModal());

  const onFileChange = async (fileData: any) => {
    const file = fileData.target.files?.[0];

    fileData.target.value = "";

    if (!file) return;

    setFileName(file.name);

    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    const headers = jsonData[0] as string[];
    const rows = jsonData.slice(1);

    setFileHeaders(headers);
    setParsedData(
      rows.map((row) => {
        const obj: Record<string, any> = {};
        headers.forEach((header, i) => {
          obj[header] = row[i];
        });
        return obj;
      })
    );
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e;
    if (file) {
      onFileChange(file);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e;
    if (file) {
      onFileChange(e);
    }
  };
  const isValidMapping = () => {
    const values = Object.values(mappedFields);
    return values.every((v) => v) && new Set(values).size === values.length;
  };
  const [createContactMutation, { loading: importLoading }] = useMutation(
    CREATE_CONTACT,
    {
      onCompleted() {
        popModal();
        dispatch(
          AppActions.showNotification(
            "Your contacts have been successfully imported",
            AppNotificationTypeEnum.Success
          )
        );
        refetchData();
      },

      onError(error) {
        if (
          error?.networkError &&
          "statusCode" in error?.networkError &&
          error?.networkError?.statusCode === 413
        ) {
          dispatch(
            AppActions.showNotification(
              "Payload limit exceeded",
              AppNotificationTypeEnum.Error
            )
          );
          return;
        }
        dispatch(
          AppActions.showNotification(
            error.message,
            AppNotificationTypeEnum.Error
          )
        );
      },
    }
  );

  const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  // const handleImport = () => {
  //   if (!isValidMapping()) return;

  //   const retriveCsvData = parsedData.map((row) => ({
  //     firstName: row[mappedFields.firstName],
  //     lastName: row[mappedFields.lastName],
  //     email: row[mappedFields.email],
  //   }));
  //   const chunks = chunkArray(retriveCsvData, 500);


  //   createContactMutation({
  //     variables: {
  //       contact: retriveCsvData,
  //     },
  //   });
  // };

const handleImport = async () => {
  if (!isValidMapping()) return;

  const contactList = parsedData.map((row) => ({
    firstName: row[mappedFields.firstName],
    lastName: row[mappedFields.lastName],
    email: row[mappedFields.email],
  }));

  const chunks = chunkArray(contactList, 5000); 

  for (let i = 0; i < chunks.length; i++) {
    try {
      await createContactMutation({
        variables: {
          contact: chunks[i],
        },
      });
    } catch (error: any) {
      dispatch(
        AppActions.showNotification(
          `Error importing contacts ${i + 1}: ${error.message}`,
          AppNotificationTypeEnum.Error
        )
      );
      break; 
    }
  }
  popModal();
  dispatch(
    AppActions.showNotification(
      "Your contacts have been successfully imported",
      AppNotificationTypeEnum.Success
    )
  );
  refetchData();
};

  const dropdownItems = fileHeaders.map((header) => ({
    text: header,
    value: header,
  }));
  const handleSelectChange = (
    field: FieldNames.FirstName | FieldNames.LastName | FieldNames.Email,
    value: string
  ) => {
    //TODO Check for duplicate values from dropdown

    const values = Object.entries(mappedFields)
      .filter(([key]) => key !== field)
      .map(([_, val]) => val);
    if (values.includes(value)) {
      dispatch(
        AppActions.showNotification(
          DropdownFieldsErrors.DuplicateFields,
          AppNotificationTypeEnum.Error
        )
      );
      return;
    }
    setMappedFields((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const handleFileClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <ModalContainer width={ModalHeaderProp.ModalContainerWidth}>
        <ModalHeader
          title={ModalHeaderProp.ModalHeaderTitle}
          close={popModal}
        />
        <ModalContent>
          <ConfirmText>
            {" "}
            You can import contacts from a CSV or Excel file. Make sure your
            data has columns for First Name, Last Name, and Email Address.
          </ConfirmText>
          <Container>
            <DropZone
              isDragging={isDragging}
              onClick={handleFileClick}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <FileLabel htmlFor="csv-upload">Choose a CSV File</FileLabel>
              <HiddenFileInput
                id="csv-upload"
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={handleFileChange}
              />
              <FileName>{fileName || "No file chosen"}</FileName>
            </DropZone>
            {fileHeaders.length > 0 && (
              <Grid>
                <Dropdown
                  label={DropdownLabels.FirstName}
                  value={mappedFields.firstName}
                  items={dropdownItems}
                  valueWhiteSpace={DropdownProps.WhiteSpace}
                  onChange={(value) =>
                    handleSelectChange(FieldNames.FirstName, value)
                  }
                />
                <Dropdown
                  label={DropdownLabels.LastName}
                  value={mappedFields.lastName}
                  items={dropdownItems}
                  valueWhiteSpace={DropdownProps.WhiteSpace}
                  onChange={(value) =>
                    handleSelectChange(FieldNames.LastName, value)
                  }
                />

                <Dropdown
                  label={DropdownLabels.Email}
                  value={mappedFields.email}
                  items={dropdownItems}
                  valueWhiteSpace={DropdownProps.WhiteSpace}
                  onChange={(value) =>
                    handleSelectChange(FieldNames.Email, value)
                  }
                />
              </Grid>
            )}
          </Container>
        </ModalContent>
        <ModalFooter>
          <div style={{ display: "flex" }}>
            <Button
              type={ButtonTypes.Thin}
              state={ButtonStates.Warning}
              text={ButtonLebels.Cancel}
              margin="0 10px 0 0"
              onClick={popModal}
            />
            <Button
              type={ButtonTypes.Thin}
              text={ButtonLebels.Import}
              onClick={handleImport}
              loading={importLoading}
              state={
                !isValidMapping() ? ButtonStates.Disabled : ButtonStates.Active
              }
            />
          </div>
        </ModalFooter>
      </ModalContainer>
    </>
  );
};
export default ImportTicketModal;
